/* eslint-disable no-underscore-dangle */
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import _ from 'lodash';
import { post } from '../../../Services/API';
import Error from '../../../Components/Error';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import setStateFromInput from '../../../Utils/setStateFromInput';
import BillingoAccountSelector from '../../../Components/Billingo/BillingoAccountSelector';

function PartnerInputModal({
  originalPartner = {}, show = false, handleClose, fetchPartners,
}) {
  const defaultPartner = {
    active: 1,
    driver_portal_enabled: 0,
    ph_branding_allowed: 0,
    visitor_tracking_step: 0,
    // No default: how a partner is invoiced is chosen explicitly. A silent
    // "monthly" left per-trip partners with no invoicing flow at all.
    invoicing_enabled: '',
    driver_events_queue: '',
    driver_events_queue_fifo: '',
    billing: {},
  };

  const [partner, setPartner] = useState(_.defaults(originalPartner, defaultPartner));
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const invoicingChosen = ['0', '1'].includes(String(partner.invoicing_enabled));

  const handleSubmit = async () => {
    if (!invoicingChosen) {
      setValidationErrors([{
        field: 'invoicing_enabled', message: 'Choose how this partner is invoiced.',
      }]);
      return;
    }
    setLoading(true);

    try {
      await post('/bookings/partners', {
        ...partner,
        active: Number(partner.active),
        ph_branding_allowed: Number(partner.ph_branding_allowed),
        driver_portal_enabled: Number(partner.driver_portal_enabled),
        visitor_tracking_step: Number(partner.visitor_tracking_step),
        invoicing_enabled: Number(partner.invoicing_enabled),
        billingo_account_id: partner.billingo_account_id || null,
        billingo_account_name: partner.billingo_account_name || null,
      });
      fetchPartners();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message);

      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const comentsTextareaRef = useRef(null);
  const paymentTermsTextareaRef = useRef(null);

  useEffect(() => {
    const comentsTextareaRefCurrent = comentsTextareaRef.current;
    if (comentsTextareaRefCurrent) {
      comentsTextareaRefCurrent.style.height = 'auto';
      comentsTextareaRefCurrent.style.height = `${comentsTextareaRefCurrent.scrollHeight}px`;
    }

    const paymentTermsTextareaRefCurrent = paymentTermsTextareaRef.current;
    if (paymentTermsTextareaRefCurrent) {
      paymentTermsTextareaRefCurrent.style.height = `${paymentTermsTextareaRefCurrent.scrollHeight}px`;
    }
  }, [partner]);

  /**
   * Handle input change
   * @param {object} e
   * @returns void
   */
  const handleChange = (e) => {
    setStateFromInput(e, setPartner, partner);
  };

  return (
    <Modal show={show} onHide={handleClose} size="md">
      <Modal.Header closeButton>
        <Modal.Title>
          { partner.Id && (
          <>
            Edit partner
            { ' ' }
            { partner.name }
          </>
          ) }
          { !partner.Id && (
          <>
            New partner
          </>
          ) }
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>

        <Error
          error={error}
          setError={setError}
        />

        <Form>
          <Form.Group className="mb-3 col-12" controlId="name">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              type="text"
              value={partner.name}
              onChange={handleChange}
            />
            <FormControlFeedback errors={validationErrors} field="name" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="website">
            <Form.Label>Website</Form.Label>
            <Form.Control
              type="text"
              value={partner.website}
              onChange={handleChange}
            />
            <FormControlFeedback errors={validationErrors} field="website" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="email">
            <Form.Label>Email address</Form.Label>
            <Form.Control
              type="email"
              value={partner.email}
              onChange={handleChange}
            />
            <FormControlFeedback errors={validationErrors} field="email" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="phone_number">
            <Form.Label>Phone Number</Form.Label>
            <Form.Control
              type="text"
              value={partner.phone_number}
              onChange={handleChange}
            />
            <FormControlFeedback errors={validationErrors} field="phone_number" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="billing.name">
            <Form.Label>Billing Name</Form.Label>
            <Form.Control
              type="text"
              value={partner.billing?.name}
              onChange={handleChange}
            />
            <FormControlFeedback errors={validationErrors} field="billing.name" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="billing.address">
            <Form.Label>Billing Address</Form.Label>
            <Form.Control
              type="text"
              value={partner.billing?.address}
              onChange={handleChange}
            />
            <FormControlFeedback errors={validationErrors} field="billing.address" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="billing.city">
            <Form.Label>Billing City</Form.Label>
            <Form.Control
              type="text"
              value={partner.billing?.city}
              onChange={handleChange}
            />
            <FormControlFeedback errors={validationErrors} field="billing.city" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="billing.postcode">
            <Form.Label>Billing Postcode</Form.Label>
            <Form.Control
              type="text"
              value={partner.billing?.postcode}
              onChange={handleChange}
            />
            <FormControlFeedback errors={validationErrors} field="billing.postcode" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="billing.country">
            <Form.Label>Billing Country</Form.Label>
            <Form.Control
              type="text"
              value={partner.billing?.country}
              onChange={handleChange}
            />
            <FormControlFeedback errors={validationErrors} field="billing.country" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="comments">
            <Form.Label>Comments</Form.Label>
            <Form.Control
              as="textarea"
              value={partner.comments}
              onChange={handleChange}
              ref={comentsTextareaRef}
              style={{ overflow: 'hidden', resize: 'none' }}
            />
            <FormControlFeedback errors={validationErrors} field="comments" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="payment_terms">
            <Form.Label>Payment Terms</Form.Label>
            <Form.Control
              as="textarea"
              value={partner.payment_terms}
              ref={paymentTermsTextareaRef}
              style={{ overflow: 'hidden', resize: 'none' }}
              onChange={handleChange}
            />
            <FormControlFeedback errors={validationErrors} field="payment_terms" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="driver_portal_enabled">
            <Form.Label>Driver Portal Enabled</Form.Label>
            <Form.Select
              value={partner.driver_portal_enabled}
              onChange={handleChange}
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </Form.Select>
            <FormControlFeedback errors={validationErrors} field="driver_portal_enabled" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="ph_branding_allowed">
            <Form.Label>Pickup Hungary Branding Allowed</Form.Label>
            <Form.Select
              value={partner.ph_branding_allowed}
              onChange={handleChange}
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </Form.Select>
            <FormControlFeedback errors={validationErrors} field="ph_branding_allowed" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="visitor_tracking_step">
            <Form.Label>Visitor Tracking Step</Form.Label>
            <Form.Select
              value={partner.visitor_tracking_step}
              onChange={handleChange}
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </Form.Select>
            <FormControlFeedback errors={validationErrors} field="visitor_tracking_step" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="invoicing_enabled">
            <Form.Label>Invoicing *</Form.Label>
            <Form.Select
              value={partner.invoicing_enabled ?? ''}
              onChange={handleChange}
            >
              <option value="" disabled>Choose how this partner is invoiced…</option>
              <option value={1}>Invoiced per trip (Billingo documents per booking)</option>
              <option value={0}>Invoiced monthly</option>
            </Form.Select>
            <FormControlFeedback errors={validationErrors} field="invoicing_enabled" />
          </Form.Group>

          {Number(partner.invoicing_enabled) === 1 && (
            <Form.Group className="mb-3 col-12">
              <Form.Label>Default Billingo account</Form.Label>
              <BillingoAccountSelector
                value={partner.billingo_account_id
                  ? { id: partner.billingo_account_id, name: partner.billingo_account_name }
                  : null}
                defaultBilling={partner.billing}
                onChange={(acct) => setPartner((p) => ({
                  ...p,
                  billingo_account_id: acct?.id || null,
                  billingo_account_name: acct?.name || null,
                }))}
              />
              <div className="text-muted small mt-1">
                Pre-selected when creating invoices for this partner (overridable per booking).
              </div>
            </Form.Group>
          )}

          <Form.Group className="mb-3 col-12" controlId="parent_partner_id">
            <Form.Label>Parent Partner UUID</Form.Label>
            <Form.Control
              value={partner.parent_partner_id}
              onChange={handleChange}
            />
            <FormControlFeedback errors={validationErrors} field="parent_partner_id" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="driver_events_queue">
            <Form.Label>Driver Events Queue URL (standard)</Form.Label>
            <Form.Control
              value={partner.driver_events_queue}
              onChange={handleChange}
            />
            <div className="text-muted small mt-1">
              The original queue. Carries the driver/car assignment only — the
              partner is not told what the driver does next.
            </div>
            <FormControlFeedback errors={validationErrors} field="driver_events_queue" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="driver_events_queue_fifo">
            <Form.Label>Driver Events Queue URL (FIFO)</Form.Label>
            <Form.Control
              value={partner.driver_events_queue_fifo}
              onChange={handleChange}
            />
            <div className="text-muted small mt-1">
              The ordered queue (its name ends
              {' '}
              <code>.fifo</code>
              ). It carries the driver&apos;s trip steps — departed, arrived,
              passenger on board, dropped off, no-show — as well as the
              assignment, in the order they happened.
              {' '}
              <strong>Filling this in switches the partner over:</strong>
              {' '}
              everything new goes here instead, while anything still sitting in
              the standard queue above finishes on its own. Empty it to switch
              back.
            </div>
            <FormControlFeedback errors={validationErrors} field="driver_events_queue_fifo" />
          </Form.Group>

          <Form.Group className="mb-3 col-12" controlId="active">
            <Form.Label>Active</Form.Label>
            <Form.Select
              value={partner.active}
              onChange={handleChange}
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </Form.Select>
            <FormControlFeedback errors={validationErrors} field="active" />
          </Form.Group>
        </Form>
        <Modal.Footer>
          <Button
            onClick={handleSubmit}
            disabled={loading}
          >
            Submit Partner
          </Button>
        </Modal.Footer>
      </Modal.Body>
    </Modal>
  );
}

export default PartnerInputModal;
